import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV, SITE, normalisePath, whatsappLink } from "@/lib/site";

type SiteHeaderProps = {
  /**
   * "dark" for the espresso-backed pages (/art), "light" for the ivory home
   * page, so the wordmark keeps its contrast against the surface behind it.
   */
  tone?: "light" | "dark";
  /** Suppress the border until the visitor scrolls, as on the home page. */
  floatUntilScroll?: boolean;
};

/**
 * The one header, shared by the home page and the standalone pages.
 *
 * Extracted so a change to the nav cannot drift between surfaces. It owns its
 * own scroll listener rather than receiving `scrolled` as a prop, because each
 * page mounts it independently.
 */
export function SiteHeader({ tone = "light", floatUntilScroll = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const dark = tone === "dark";

  useEffect(() => {
    if (!floatUntilScroll) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [floatUntilScroll]);

  // Which standalone page (if any) owns the current URL, so its nav item can
  // hold the active treatment.
  const current = normalisePath(window.location.pathname);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-all duration-500",
        floatUntilScroll && !scrolled && "border-b border-transparent",
        floatUntilScroll && scrolled
          ? "border-b border-border/70 bg-background/85 backdrop-blur-md"
          : !floatUntilScroll &&
            (dark
              ? "border-b border-ivory/10 bg-espresso/85 backdrop-blur-md"
              : "border-b border-border/70 bg-background/85 backdrop-blur-md"),
      )}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 lg:px-10">
        <a
          href="/"
          className={cn("group flex flex-col leading-none", dark && "text-ivory")}
          aria-label={`${SITE.name} — home`}
        >
          <span className="font-display text-lg tracking-[0.2em] uppercase">{SITE.name}</span>
          <span
            className={cn(
              "mt-1 font-sans text-[0.5625rem] tracking-[0.34em] uppercase",
              dark ? "text-ivory/55" : "text-muted-foreground",
            )}
          >
            {SITE.role}
          </span>
        </a>

        <ul className="hidden items-center gap-7 md:flex lg:gap-9">
          {NAV.map(item => {
            // Route items are always real links; section items only make sense
            // on the home page, so they are plain text there.
            const isRoute = Boolean(item.route);
            const isActive = isRoute && current === item.href;
            const classes = cn(
              "link-underline font-sans text-[0.6875rem] tracking-[0.22em] uppercase transition-colors duration-300",
              dark ? "text-ivory/75 hover:text-gold" : "hover:text-gold",
              isActive && "text-gold",
            );

            return (
              <li key={item.id}>
                {item.action === "book" ? (
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes}
                  >
                    {item.label}
                  </a>
                ) : isRoute ? (
                  <a
                    href={item.href}
                    aria-current={isActive ? "page" : undefined}
                    className={classes}
                  >
                    {item.label}
                  </a>
                ) : (
                  <a href={current === "/" ? item.href : `/${item.href}`} className={classes}>
                    {item.label}
                  </a>
                )}
              </li>
            );
          })}
        </ul>

        <a
          href={whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "group inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-sans text-[0.6875rem] tracking-[0.2em] uppercase transition-all duration-500",
            dark
              ? "border-ivory/30 text-ivory hover:border-ivory hover:bg-ivory hover:text-espresso"
              : "border-foreground/25 hover:border-foreground hover:bg-foreground hover:text-background",
          )}
        >
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
          Book
        </a>
      </nav>
    </header>
  );
}
